import { getAdobeAccessToken } from '../auth/adobeIms';

export const TRACKER_ACTION_BASE_URL = 'https://55338-856salmonanaconda-stage.adobeioruntime.net/api/v1/web/ondemand-tracker';

export interface TrackerUser {
  _id: string;        // email (used as primary key)
  name?: string;
  accessLevel: string;
  enabled: boolean;
  createdAt?: string;
  lastUpdated?: string;
}

export interface TrackerContributor {
  id: string;
  name: string;
  email?: string;
  region?: string;
  displayOrder?: number | null;
  isOutOfOffice?: boolean;
  oooStartDate?: string;
  oooEndDate?: string;
  lastUpdated?: string;
}

export interface TrackerAuditLogEntry {
  _id: string;
  timestamp: string;
  actionType: string;
  actorId: string;
  accessLevel?: string;
  [key: string]: unknown;
}

export interface TrackerAuditLogResponse {
  logs: TrackerAuditLogEntry[];
  totalCount: number;
  offset: number;
  limit: number;
}

export type TrackerAvailabilityStatus = 'available' | 'limited' | 'unavailable';

export interface TrackerAvailabilityRange {
  startDate: string;
  endDate: string;
  status: TrackerAvailabilityStatus;
}

export interface TrackerSolution {
  id: string;
  name: string;
  displayOrder?: number | null;
}

/**
 * Full shape of the tracker's db-get-state response — contributor roster plus
 * per-solution availability, date-range overrides, today's schedule, and
 * experience levels, all keyed by contributor id.
 */
export interface TrackerAvailabilityState {
  availability?: Record<string, Record<string, TrackerAvailabilityStatus>>;
  dateAvailability?: Record<string, Record<string, TrackerAvailabilityRange[]>>;
  weeklyScheduleToday?: Record<string, Record<string, TrackerAvailabilityStatus>>;
  experienceLevels?: Record<string, Record<string, string>>;
  contributors?: TrackerContributor[];
  solutions?: TrackerSolution[];
}

function trackerActionUrl(action: string): string {
  return `${TRACKER_ACTION_BASE_URL.replace(/\/$/, '')}/${action}`;
}

function authHeaders(token: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

/**
 * Fetch the tracker's full state (contributor roster + availability layers +
 * experience levels + solutions). Calls the tracker's db-get-state action.
 */
export async function trackerGetAvailabilityState(): Promise<TrackerAvailabilityState> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(trackerActionUrl('db-get-state'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error((errBody as { error?: string }).error || `HTTP ${response.status}`);
    }
    return await response.json() as TrackerAvailabilityState;
  } catch (error) {
    console.error('trackerGetAvailabilityState error:', error);
    throw error;
  }
}

/**
 * Fetch the tracker's contributor roster.
 * Calls the tracker's db-get-state action and returns its contributors.
 */
export async function trackerGetContributors(): Promise<TrackerContributor[]> {
  try {
    const state = await trackerGetAvailabilityState();
    return state.contributors ?? [];
  } catch (error) {
    console.error('trackerGetContributors error:', error);
    throw error;
  }
}

/**
 * Fetch all users from the tracker app.
 * Calls the tracker's admin-list-users action.
 */
export async function trackerListUsers(): Promise<TrackerUser[]> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(trackerActionUrl('admin-list-users'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error((errBody as { error?: string }).error || `HTTP ${response.status}`);
    }
    const data = await response.json() as { users: TrackerUser[] };
    return data.users ?? [];
  } catch (error) {
    console.error('trackerListUsers error:', error);
    throw error;
  }
}

/**
 * Upsert a user in the tracker app.
 * Calls the tracker's admin-manage-users action with operation: 'upsert'.
 */
export async function trackerUpsertUser(user: {
  email: string;
  name?: string;
  accessLevel: string;
}): Promise<void> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(trackerActionUrl('admin-manage-users'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ operation: 'upsert', user }),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error((errBody as { error?: string }).error || `HTTP ${response.status}`);
    }
  } catch (error) {
    console.error('trackerUpsertUser error:', error);
    throw error;
  }
}

export async function trackerToggleUser(
  targetEmail: string,
  enabled: boolean
): Promise<void> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(trackerActionUrl('admin-manage-users'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({
        operation: 'upsert',
        user: { email: targetEmail, enabled },
      }),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error((errBody as { error?: string }).error || `HTTP ${response.status}`);
    }
  } catch (error) {
    console.error('trackerToggleUser error:', error);
    throw error;
  }
}

/**
 * Fetch audit log entries from the tracker app.
 * Calls the tracker's admin-list-audit-logs action.
 */
export async function trackerListAuditLogs(params: {
  offset?: number;
  limit?: number;
  actionType?: string;
  actorEmail?: string;
  startDate?: string;
  endDate?: string;
} = {}): Promise<TrackerAuditLogResponse> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(trackerActionUrl('admin-list-audit-logs'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error((errBody as { error?: string }).error || `HTTP ${response.status}`);
    }
    return await response.json() as TrackerAuditLogResponse;
  } catch (error) {
    console.error('trackerListAuditLogs error:', error);
    throw error;
  }
}
